// Cliente HTTP hacia Twenty. Agrega el token de la sesion, renueva el token si vence (401)
// y avisa a la app cuando la sesion ya no sirve.

import { KEYS, readJSON, remove, writeJSON } from '../lib/storage';

export type Session = {
  modo: 'usuario' | 'apikey';
  accessToken: string;
  refreshToken?: string;
  expiresAt?: string;
  email?: string;
  workspace: { id?: string; nombre: string; url?: string; logo?: string };
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Listener = (s: Session | null) => void;
const listeners = new Set<Listener>();
let current: Session | null = readJSON<Session | null>(KEYS.sesion, null);

export const getSession = () => current;

export function setSession(s: Session | null) {
  current = s;
  if (s) writeJSON(KEYS.sesion, s);
  else {
    remove(KEYS.sesion);
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k?.startsWith('consola.cache.')) localStorage.removeItem(k);
      }
    } catch {
      /* sin almacenamiento */
    }
  }
  listeners.forEach((l) => l(s));
}

export function onSessionChange(l: Listener) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

// GraphQL de Twenty para autenticacion y datos del usuario (/metadata).
export async function metadataQuery<T>(query: string, variables?: Record<string, unknown>, token?: string): Promise<T> {
  const res = await fetch('/metadata', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ query, variables }),
  });
  type Amigable = string | { message?: string } | undefined;
  const json = (await res.json().catch(() => ({}))) as { data?: T; errors?: { message: string; extensions?: { userFriendlyMessage?: Amigable; code?: string } }[] };
  if (json.errors?.length) {
    const e = json.errors[0];
    const code = e.extensions?.code === 'UNAUTHENTICATED' ? 401 : res.status === 200 ? 400 : res.status;
    // Twenty 2.x manda el mensaje amigable como texto o como { id, message } (mensaje traducible).
    const amigable = e.extensions?.userFriendlyMessage;
    const texto = typeof amigable === 'string' ? amigable : amigable?.message;
    throw new ApiError(code, traducirError(texto && texto !== 'An error occurred.' ? texto : e.message), json.errors);
  }
  if (!res.ok || !json.data) throw new ApiError(res.status, `El servidor respondió ${res.status}`);
  return json.data;
}

// Mensajes de Twenty que ve el usuario, en espanol claro.
export function traducirError(mensaje: unknown) {
  if (typeof mensaje !== 'string') return 'El servidor del CRM respondió con un error.';
  const m = mensaje.toLowerCase();
  if (m.includes('user not found') || m.includes('wrong password') || m.includes('invalid credentials'))
    return 'El correo o la contraseña no coinciden.';
  if (m.includes('workspace not found')) return 'No encontramos esa empresa en el servidor.';
  if (m.includes('origin is not allowed')) return 'El servidor del CRM rechazó el origen de la Consola: el proxy debe quitar el encabezado Origin (ver consola/README.md).';
  if (m.includes('captcha')) return 'El servidor pide una verificación adicional; contacta al administrador del CRM.';
  if (m.includes('email is not verified')) return 'Primero verifica tu correo; si no te llegó el mensaje, pide ayuda al administrador del CRM.';
  if (m.includes('token has expired') || m.includes('expired')) return 'Tu sesión venció. Vuelve a entrar.';
  if (m.includes('failed to fetch') || m.includes('networkerror')) return 'No hay conexión con el servidor del CRM.';
  return mensaje;
}

// Resultado de renovar: 'ok' (hay token nuevo), 'rechazado' (el servidor dijo que la sesion ya no sirve: hay que
// volver a entrar) o 'error' (red o servidor caido: NO se cierra la sesion, se reintenta despues).
type Renovacion = 'ok' | 'rechazado' | 'error';
let refreshing: Promise<Renovacion> | null = null;

async function refresh(): Promise<Renovacion> {
  const s = current;
  if (!s?.refreshToken) return 'rechazado';
  refreshing ??= (async (): Promise<Renovacion> => {
    try {
      const data = await metadataQuery<{ renewToken: { tokens: { accessOrWorkspaceAgnosticToken: { token: string; expiresAt: string }; refreshToken: { token: string } } } }>(
        `mutation Renovar($appToken: String!) { renewToken(appToken: $appToken) { tokens { accessOrWorkspaceAgnosticToken { token expiresAt } refreshToken { token } } } }`,
        { appToken: s.refreshToken },
      );
      const t = data.renewToken.tokens;
      setSession({ ...s, accessToken: t.accessOrWorkspaceAgnosticToken.token, expiresAt: t.accessOrWorkspaceAgnosticToken.expiresAt, refreshToken: t.refreshToken.token });
      return 'ok';
    } catch (e) {
      // 4xx del servidor (token de renovacion vencido o revocado): la sesion termino. Lo demas es transitorio.
      return e instanceof ApiError && e.status >= 400 && e.status < 500 ? 'rechazado' : 'error';
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

// El acceso dura poco: se renueva un minuto antes de vencer para que las consultas no choquen con un 401.
async function renovarSiVence() {
  const s = current;
  if (s?.modo !== 'usuario' || !s.expiresAt || !s.refreshToken) return;
  if (Date.parse(s.expiresAt) - Date.now() > 60_000) return;
  if ((await refresh()) === 'rechazado') setSession(null);
}

// Tras un 401: si otra consulta ya renovo el token mientras tanto, basta con repetir; si no, se renueva.
async function recuperarDe401(tokenUsado: string): Promise<'repetir' | 'salir' | 'fallo'> {
  if (current?.modo !== 'usuario') return 'salir';
  if (current.accessToken !== tokenUsado) return 'repetir';
  const r = await refresh();
  return r === 'ok' ? 'repetir' : r === 'rechazado' ? 'salir' : 'fallo';
}

// /metadata con la sesion actual: si el acceso vencio, lo renueva y repite una vez (como request() con REST).
// Sin esto, "quien soy", usuarios y llaves fallaban justo despues de que el token vencia.
export async function metadataSesion<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  await renovarSiVence();
  const token = current?.accessToken ?? '';
  try {
    return await metadataQuery<T>(query, variables, token);
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) throw e;
    const r = await recuperarDe401(token);
    if (r === 'repetir') return metadataQuery<T>(query, variables, current?.accessToken);
    if (r === 'salir') setSession(null);
    throw r === 'fallo' ? new ApiError(0, 'No hay conexión con el servidor del CRM. Reintenta en un momento.') : e;
  }
}

// Archivos (blobs) con la sesion: renueva antes si vence y repite una vez tras un 401.
export async function archivoConSesion(path: string): Promise<Blob> {
  await renovarSiVence();
  const pedir = () => fetch(path, { headers: current ? { Authorization: `Bearer ${current.accessToken}` } : {} });
  const token = current?.accessToken ?? '';
  let r = await pedir();
  if (r.status === 401 && (await recuperarDe401(token)) === 'repetir') r = await pedir();
  if (!r.ok) {
    // El servicio explica el motivo (p. ej. 410: el archivo esta en la papelera hasta tal fecha).
    const j = (await r.json().catch(() => null)) as { mensaje?: string } | null;
    throw new ApiError(r.status, j?.mensaje || `No se pudo abrir el archivo (${r.status}).`);
  }
  return r.blob();
}

// Respuesta en vivo (eventos del asistente): POST con la sesion vigente; quien llama lee el cuerpo por partes.
export async function flujoConSesion(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
  await renovarSiVence();
  const pedir = () => fetch(path, { method: 'POST', signal, headers: { 'Content-Type': 'application/json', ...(current ? { Authorization: `Bearer ${current.accessToken}` } : {}) }, body: JSON.stringify(body) });
  const token = current?.accessToken ?? '';
  let r = await pedir();
  if (r.status === 401 && (await recuperarDe401(token)) === 'repetir') r = await pedir();
  return r;
}

// Un archivo tal cual (sin JSON) con la sesion vigente, p. ej. los adjuntos del chat de Amix. Devuelve el JSON de la
// respuesta o lanza ApiError con el motivo que da el servicio.
// Un archivo en binario a un servicio propio, con la sesion (y un reintento si el token vencio). Devuelve la respuesta tal cual.
export async function enviarConSesion(path: string, archivo: Blob, headers: Record<string, string> = {}): Promise<Response> {
  await renovarSiVence();
  const pedir = () => fetch(path, { method: 'POST', body: archivo, headers: { 'Content-Type': archivo.type || 'application/octet-stream', ...headers, ...(current ? { Authorization: `Bearer ${current.accessToken}` } : {}) } });
  const token = current?.accessToken ?? '';
  let r = await pedir();
  if (r.status === 401 && (await recuperarDe401(token)) === 'repetir') r = await pedir();
  return r;
}

export async function subirConSesion<T>(path: string, archivo: Blob, headers: Record<string, string> = {}): Promise<T> {
  const r = await enviarConSesion(path, archivo, headers);
  const j = (await r.json().catch(() => null)) as (T & { mensaje?: string }) | null;
  if (!r.ok || !j) throw new ApiError(r.status, j?.mensaje || `No se pudo subir el archivo (${r.status}).`);
  return j;
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  // Sin reintentar los 429: el servicio limita a proposito (p. ej. la boveda, 30 claves por minuto) y se muestra el aviso.
  sinReintento?: boolean;
};

const espera = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Un 401 que cierra la sesion: el del motor. Los servicios propios (correo, voz, CDR) y las funciones de la app (/s/,
// p. ej. responder por Zammad) dan 401 por sus propios motivos; cerrar la sesion ahi sacaba a la persona y perdia lo
// que estaba escribiendo (D56).
export const cierraSesion = (path: string) => !['/correo/', '/voz/', '/cdr/', '/s/', '/mcp/', '/vigia/', '/mensajeria/', '/rt/'].some((p) => path.startsWith(p));

export async function request<T = unknown>(path: string, opts: RequestOptions = {}, retried = false, intento = 0): Promise<T> {
  if (!retried && intento === 0) await renovarSiVence();
  const s = current;
  if (!s) throw new ApiError(401, 'Sin sesión');
  const qs = opts.query
    ? '?' + Object.entries(opts.query).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')
    : '';
  let res: Response;
  try {
    res = await fetch(path + (qs === '?' ? '' : qs), {
      method: opts.method ?? 'GET',
      signal: opts.signal,
      headers: {
        Authorization: `Bearer ${s.accessToken}`,
        ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'No hay conexión con el servidor del CRM.');
  }
  if (res.status === 401 && !retried) {
    const r = await recuperarDe401(s.accessToken);
    if (r === 'repetir') return request<T>(path, opts, true, intento);
    if (r === 'fallo') throw new ApiError(0, 'No hay conexión con el servidor del CRM. Reintenta en un momento.');
  }
  // Twenty limita las consultas con API key (100 por minuto por empresa). Se espera y se reintenta:
  // la peticion rechazada no se ejecuto, asi que reintentar tambien es seguro para escrituras.
  if (res.status === 429 && intento < 3 && !opts.sinReintento) {
    const tras = Number(res.headers.get('retry-after')) * 1000 || 4000 * (intento + 1);
    await espera(Math.min(tras, 20_000));
    return request<T>(path, opts, retried, intento + 1);
  }
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = text;
  }
  if (!res.ok) {
    // Solo se cierra la sesion si el 401 viene del motor con el token vigente (no de un servicio propio que fallo).
    if (res.status === 401 && s.modo === 'usuario' && current?.accessToken === s.accessToken && cierraSesion(path)) setSession(null);
    const j = json as { messages?: string[]; message?: string; error?: string; mensaje?: string } | null;
    // Twenty manda {error: nombre de la excepcion (a veces solo "Error"), messages: [el texto real]}: va primero el texto.
    const msg = j?.mensaje || j?.messages?.[0] || j?.message || j?.error || `El servidor respondió ${res.status}`;
    throw new ApiError(res.status, traducirError(String(msg)), json);
  }
  return json as T;
}
